import { getCurrentEnv } from "../common/utils";
import { INDY_SANDBOX_URLS } from "./indySandboxes";

interface Config {
  RECORDS_BUCKET_NAME: string;
  RESOURCES_BUCKET_NAME: string;
  DOCTOR_PROFILES_BUCKET_NAME: string;
  TEMP_DOCUMENTS_BUCKET_NAME: string;
  RECORDS_DOC_FILE_LAMBDA_FUNCTION: string;
  GET_UNRESOLVED_COMMENTS_COUNT_LAMBDA_FUNCTION: string;
  RECORDS_CREATE_APPOINTMENT_DOCS_LAMBDA_FUNCTION: string;
  RECORDS_RESUMMARIZE_SEGMENT_LAMBDA_FUNCTION: string;
  RECORDS_SUMMARIZE_SUB_ROSA_ENTRY_LAMBDA_FUNCTION: string;
  // Triage V2 (agent-orchestrated) lambda — dev-only PoC; empty in stage/prod.
  TRIAGE_V2_LAMBDA_FUNCTION: string;
  RECORDS_CREATE_EMPTY_MEDICAL_RECORD_LAMBDA_FUNCTION: string;
  BILLING_PDF_GENERATION_LAMBDA_FUNCTION: string;
  EMAIL_GENERATION_LAMBDA_FUNCTION: string;
  RECORDS_APPOINTMENT_PDF_PARSER_LAMBDA_FUNCTION: string;
  HOI_GENERATION_DYNAMIC_LAMBDA_FUNCTION: string;
  IMPAIRMENT_RATING_LAMBDA_FUNCTION: string;
  NOTICE_LETTER_GENERATION_LAMBDA_FUNCTION: string;
  NOTIFICATION_FORM_GENERATION_LAMBDA_FUNCTION: string;
  DIAGNOSTIC_STUDY_PRESCRIPTION_LAMBDA_FUNCTION: string;
  AUTO_GENERATE_DIAGNOSTIC_PRESCRIPTIONS_LAMBDA_FUNCTION: string;
  RTW_VOUCHER_FORM_LAMBDA_FUNCTION: string;
  DIAGNOSTIC_TEST_LAMBDA_FUNCTION: string;
  GENERATE_RECAP_LAMBDA_FUNCTION: string;
  GENERATE_PDF_PACKAGE_FUNCTION: string;
  GENERATE_REPORT_EMAIL_FUNCTION: string;
  HOI_APPEND_BODY_PARTS_LAMBDA_FUNCTION: string;
  DUPLICATE_GOOGLE_DOC_LAMBDA_FUNCTION: string;
  GENERATE_DIAGNOSTIC_PRESCRIPTION_LINK_LAMBDA_FUNCTION: string;
  GENERATE_DIAGNOSTIC_TEST_FORM_GOOGLE_DOC_LAMBDA_FUNCTION: string;
  GENERATE_PAST_INJURY_MATRIX_LAMBDA_FUNCTION: string;
  GENERATE_COVER_LETTER_RECAP_LAMBDA_FUNCTION: string;
  CONSOLIDATE_PARTY_LAMBDA_FUNCTION: string;
  GENERATE_INTRO_PARAGRAPH_LAMBDA_FUNCTION: string;
  GENERATE_ALL_COVER_LETTER_LAMBDA_FUNCTION: string;
  GENERATE_RECORD_BRIEFING_GOOGLE_DOC_LINK_LAMBDA_FUNCTION: string;
  GENERATE_RECORD_BRIEFING_DOCX_LAMBDA_FUNCTION: string;
  ASSESSMENT_REVIEW_GEN_LAMBDA_FUNCTION: string;
  ASSESSMENT_DICTATION_POPULATE_LAMBDA_FUNCTION: string;
  GENERATE_HISTORY_OF_INJURY_INSIGHTS_LAMBDA_FUNCTION: string;
  GENERATE_RECORD_BRIEFING_INSIGHTS_LAMBDA_FUNCTION: string;
  GENERATE_PHYSICAL_EXAM_INSIGHTS_LAMBDA_FUNCTION: string;
  GENERATE_PHYSICAL_EXAM_PREVIEW_LAMBDA_FUNCTION: string;
  GENERATE_PRE_HISTORY_TEMPLATE_LAMBDA_FUNCTION: string;
  GENERATE_JOB_DUTIES_LAMBDA_FUNCTION: string;
  FORMAT_ADDRESS_LAMBDA_FUNCTION: string;
  RESOLVE_ATTACHMENT_REVIEW_LAMBDA_FUNCTION: string;
  MERGE_UPLOADED_FILES_LAMBDA_FUNCTION: string;
  CORRECT_MEDICATIONS_LAMBDA_FUNCTION: string;
  GENERATE_HISTORY_VALIDATION_LAMBDA_FUNCTION: string;
  GEN_HISTORY_TRANSCRIPT_POPULATE_LAMBDA_FUNCTION: string;
  GEN_HISTORY_MERGE_POPULATED_SOURCES_LAMBDA_FUNCTION: string;
  ZENDESK_VALIDATE_TICKET_FUNCTION: string;
  EMIT_APPOINTMENT_EVENT_LAMBDA_FUNCTION: string;
  BACKFILL_ONSITE_OPS_LAMBDA_FUNCTION: string;
  GENERATE_MEDICAL_RECORD_VALIDATION_LAMBDA_FUNCTION: string;
  GENERATE_HISTORY_MRR_CROSS_VALIDATION_LAMBDA_FUNCTION: string;
  CALCULATE_DIFFICULTY_LEVEL_LAMBDA_FUNCTION: string;
  JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION: string;
  VECTOR_SEARCH_DEBUG_LAMBDA_FUNCTION: string;
  /** Empty in prod — the create-test-appointment lambda is not deployed there. */
  CREATE_TEST_APPOINTMENT_LAMBDA_FUNCTION: string;
  AGENT_CHAT_FUNCTION_URL: string;
  IMPERSONATION_LAMBDA_FUNCTION: string;
  COMPLIANCE_REPORT_API_LAMBDA_FUNCTION: string;
  PROMPT_EXECUTIONS_BUCKET_NAME: string;
  API_SERVER_BASE_URL: string;
  /**
   * The Indigo Doc collab service's WebSocket (specs/indigo-doc/collab-service.md).
   * Empty in every env until poc Step 2 deploys it behind
   * `collab-<env>.getindigo.io`; see `resolveCollabWsUrl` for how dev fills it.
   * Empty means the `/docs` editor opens a doc whose content isn't persisted.
   */
  /**
   * Indigo Doc collab service. Set in every env, like every other backend URL
   * here: the collab service's CDK stack is not env-gated either, so all three
   * exist. What gates the *feature* is the `enable-indigo-doc-sandbox` flag
   * (`SandboxGate`), which is the one switch worth having — an empty URL would
   * be a second, invisible gate that makes the editor open without persistence
   * and look broken the first time someone turns the flag on outside dev.
   */
  COLLAB_WS_URL: string;
}

const devConfig: Config = {
  RECORDS_BUCKET_NAME: "portal-medical-records7373a-dev",
  RESOURCES_BUCKET_NAME: "indigo-resources-dev",
  DOCTOR_PROFILES_BUCKET_NAME: "doctor-profiles-dev",
  TEMP_DOCUMENTS_BUCKET_NAME: "indigo-temp-documents-dev",
  RECORDS_DOC_FILE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateGoogleDocLinkFunction",
  GET_UNRESOLVED_COMMENTS_COUNT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GetUnresolvedCommentsCountFunction",
  RECORDS_CREATE_APPOINTMENT_DOCS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-CreateAppointmentDocsFunction",
  RECORDS_RESUMMARIZE_SEGMENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-ResummarizeSegmentFunction",
  RECORDS_SUMMARIZE_SUB_ROSA_ENTRY_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-SummarizeSubRosaEntryFunction",
  TRIAGE_V2_LAMBDA_FUNCTION: "IndigoLambdaStack-dev-ZendeskTriageV2Function",
  RECORDS_CREATE_EMPTY_MEDICAL_RECORD_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-CreateEmptyMedicalRecordFunction",
  BILLING_PDF_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateBillingFormFunction",
  EMAIL_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateEmailFunction",
  RECORDS_APPOINTMENT_PDF_PARSER_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-PdfParserFunction",
  HOI_GENERATION_DYNAMIC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateDynamicHoiFunction",
  IMPAIRMENT_RATING_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-ImpairmentRatingFunction",
  NOTICE_LETTER_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateNoticeLetterFunction",
  NOTIFICATION_FORM_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateNotificationFormFunction",
  DIAGNOSTIC_STUDY_PRESCRIPTION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateDiagnosticPrescriptionFunction",
  AUTO_GENERATE_DIAGNOSTIC_PRESCRIPTIONS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-AutoGenDiagnosticPrescriptionsFunction",
  RTW_VOUCHER_FORM_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateRtwVoucherFormFunction",
  DIAGNOSTIC_TEST_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateDiagnosticTestFunction",
  GENERATE_RECAP_LAMBDA_FUNCTION: "IndigoLambdaStack-dev-GenerateRecapFunction",
  GENERATE_PDF_PACKAGE_FUNCTION:
    "IndigoLambdaStack-dev-GeneratePdfPackageFunction",
  GENERATE_REPORT_EMAIL_FUNCTION:
    "IndigoLambdaStack-dev-GenerateReportEmailFunction",
  HOI_APPEND_BODY_PARTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-HoiAppendBodyPartsFunction",
  DUPLICATE_GOOGLE_DOC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-DuplicateGoogleDocFunction",
  GENERATE_DIAGNOSTIC_PRESCRIPTION_LINK_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateDiagnosticPrescriptionLink",
  GENERATE_DIAGNOSTIC_TEST_FORM_GOOGLE_DOC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateDiagnosticTestFormGoogleDoc",
  GENERATE_PAST_INJURY_MATRIX_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GeneratePastInjuryMatrixFunction",
  GENERATE_COVER_LETTER_RECAP_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateCoverLetterRecapFunction",
  CONSOLIDATE_PARTY_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-ConsolidatePartyFunction",
  GENERATE_INTRO_PARAGRAPH_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateIntroParagraphFunction",
  GENERATE_ALL_COVER_LETTER_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateAllCoverLetterFunction",
  GENERATE_RECORD_BRIEFING_GOOGLE_DOC_LINK_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateRecordBriefingGDocLinkFunction",
  GENERATE_RECORD_BRIEFING_DOCX_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateRecordBriefingDocxFunction",
  ASSESSMENT_REVIEW_GEN_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-AssessmentReviewGenFunction",
  ASSESSMENT_DICTATION_POPULATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-AssessmentDictationPopulateFunction",
  GENERATE_HISTORY_OF_INJURY_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateHistoryOfInjuryInsightsFunction",
  GENERATE_RECORD_BRIEFING_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateRecordBriefingInsightsFunction",
  GENERATE_PHYSICAL_EXAM_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GeneratePhysicalExamInsightsFunction",
  GENERATE_PHYSICAL_EXAM_PREVIEW_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GeneratePhysicalExamPreviewFunction",
  GENERATE_PRE_HISTORY_TEMPLATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GeneratePreHistoryTemplateFunction",
  GENERATE_JOB_DUTIES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateJobDutiesFunction",
  FORMAT_ADDRESS_LAMBDA_FUNCTION: "IndigoLambdaStack-dev-FormatAddressFunction",
  RESOLVE_ATTACHMENT_REVIEW_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-ResolveAttachmentReviewFunction",
  MERGE_UPLOADED_FILES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-MergeUploadedFilesFunction",
  CORRECT_MEDICATIONS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-CorrectMedicationsFunction",
  GENERATE_HISTORY_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateHistoryValidationFunction",
  GEN_HISTORY_TRANSCRIPT_POPULATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenHistoryTranscriptPopulateFunc",
  GEN_HISTORY_MERGE_POPULATED_SOURCES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenHistoryMergePopulatedSourcesFunc",
  ZENDESK_VALIDATE_TICKET_FUNCTION:
    "IndigoLambdaStack-dev-ValidateZendeskTicketFunction",
  EMIT_APPOINTMENT_EVENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-EmitAppointmentEventFunction",
  BACKFILL_ONSITE_OPS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-BackfillOnsiteOpsFunction",
  GENERATE_MEDICAL_RECORD_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenerateMedicalRecordValidationFunction",
  GENERATE_HISTORY_MRR_CROSS_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GenHistoryMrrValidationFunc",
  CALCULATE_DIFFICULTY_LEVEL_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-CalculateDifficultyLevelFunction",
  JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-GetJotformSubmissionFunction",
  VECTOR_SEARCH_DEBUG_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-VectorSearchDebugFunction",
  CREATE_TEST_APPOINTMENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-dev-CreateTestAppointmentFunction",
  AGENT_CHAT_FUNCTION_URL:
    "https://umynevcyc6rrpjkbanveejbllu0ouoxw.lambda-url.us-east-1.on.aws/",
  IMPERSONATION_LAMBDA_FUNCTION: "IndigoLambdaStack-dev-ImpersonationFunction",
  COMPLIANCE_REPORT_API_LAMBDA_FUNCTION: "ComplianceReportStack-dev-Api",
  PROMPT_EXECUTIONS_BUCKET_NAME: "indigo-prompt-executions-dev",

  API_SERVER_BASE_URL: "https://api-dev.getindigo.io",
  COLLAB_WS_URL: "wss://collab-dev.getindigo.io",
};

const stageConfig: Config = {
  RECORDS_BUCKET_NAME: "portal-medical-recordsab1b4-stage",
  RESOURCES_BUCKET_NAME: "indigo-resources-stage",
  DOCTOR_PROFILES_BUCKET_NAME: "doctor-profiles-stage",
  TEMP_DOCUMENTS_BUCKET_NAME: "indigo-temp-documents-stage",
  RECORDS_DOC_FILE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateGoogleDocLinkFunction",
  GET_UNRESOLVED_COMMENTS_COUNT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GetUnresolvedCommentsCountFunction",
  RECORDS_CREATE_APPOINTMENT_DOCS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-CreateAppointmentDocsFunction",
  RECORDS_RESUMMARIZE_SEGMENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-ResummarizeSegmentFunction",
  RECORDS_SUMMARIZE_SUB_ROSA_ENTRY_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-SummarizeSubRosaEntryFunction",
  TRIAGE_V2_LAMBDA_FUNCTION: "",
  RECORDS_CREATE_EMPTY_MEDICAL_RECORD_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-CreateEmptyMedicalRecordFunction",
  BILLING_PDF_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateBillingFormFunction",
  EMAIL_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateEmailFunction",
  RECORDS_APPOINTMENT_PDF_PARSER_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-PdfParserFunction",
  HOI_GENERATION_DYNAMIC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateDynamicHoiFunction",
  IMPAIRMENT_RATING_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-ImpairmentRatingFunction",
  NOTICE_LETTER_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateNoticeLetterFunction",
  NOTIFICATION_FORM_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateNotificationFormFunction",
  DIAGNOSTIC_STUDY_PRESCRIPTION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateDiagnosticPrescriptionFunction",
  AUTO_GENERATE_DIAGNOSTIC_PRESCRIPTIONS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-AutoGenDiagnosticPrescriptionsFunction",
  RTW_VOUCHER_FORM_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateRtwVoucherFormFunction",
  DIAGNOSTIC_TEST_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateDiagnosticTestFunction",
  GENERATE_RECAP_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateRecapFunction",
  GENERATE_PDF_PACKAGE_FUNCTION:
    "IndigoLambdaStack-stage-GeneratePdfPackageFunction",
  GENERATE_REPORT_EMAIL_FUNCTION:
    "IndigoLambdaStack-stage-GenerateReportEmailFunction",
  HOI_APPEND_BODY_PARTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-HoiAppendBodyPartsFunction",
  DUPLICATE_GOOGLE_DOC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-DuplicateGoogleDocFunction",
  GENERATE_DIAGNOSTIC_PRESCRIPTION_LINK_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateDiagnosticPrescriptionLink",
  GENERATE_DIAGNOSTIC_TEST_FORM_GOOGLE_DOC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateDiagnosticTestFormGoogleDoc",
  GENERATE_PAST_INJURY_MATRIX_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GeneratePastInjuryMatrixFunction",
  GENERATE_COVER_LETTER_RECAP_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateCoverLetterRecapFunction",
  CONSOLIDATE_PARTY_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-ConsolidatePartyFunction",
  GENERATE_INTRO_PARAGRAPH_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateIntroParagraphFunction",
  GENERATE_ALL_COVER_LETTER_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateAllCoverLetterFunction",
  GENERATE_RECORD_BRIEFING_GOOGLE_DOC_LINK_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateRecordBriefingGDocLinkFunction",
  GENERATE_RECORD_BRIEFING_DOCX_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateRecordBriefingDocxFunction",
  ASSESSMENT_REVIEW_GEN_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-AssessmentReviewGenFunction",
  ASSESSMENT_DICTATION_POPULATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-AssessmentDictationPopulateFunction",
  GENERATE_HISTORY_OF_INJURY_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateHistoryOfInjuryInsightsFunction",
  GENERATE_RECORD_BRIEFING_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateRecordBriefingInsightsFunction",
  GENERATE_PHYSICAL_EXAM_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GeneratePhysicalExamInsightsFunction",
  GENERATE_PHYSICAL_EXAM_PREVIEW_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GeneratePhysicalExamPreviewFunction",
  GENERATE_PRE_HISTORY_TEMPLATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GeneratePreHistoryTemplateFunction",
  GENERATE_JOB_DUTIES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateJobDutiesFunction",
  FORMAT_ADDRESS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-FormatAddressFunction",
  RESOLVE_ATTACHMENT_REVIEW_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-ResolveAttachmentReviewFunction",
  MERGE_UPLOADED_FILES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-MergeUploadedFilesFunction",
  CORRECT_MEDICATIONS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-CorrectMedicationsFunction",
  GENERATE_HISTORY_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateHistoryValidationFunction",
  GEN_HISTORY_TRANSCRIPT_POPULATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenHistoryTranscriptPopulateFunc",
  GEN_HISTORY_MERGE_POPULATED_SOURCES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenHistoryMergePopulatedSourcesFunc",
  ZENDESK_VALIDATE_TICKET_FUNCTION:
    "IndigoLambdaStack-stage-ValidateZendeskTicketFunction",
  EMIT_APPOINTMENT_EVENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-EmitAppointmentEventFunction",
  BACKFILL_ONSITE_OPS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-BackfillOnsiteOpsFunction",
  GENERATE_MEDICAL_RECORD_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenerateMedicalRecordValidationFunction",
  GENERATE_HISTORY_MRR_CROSS_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GenHistoryMrrValidationFunc",
  CALCULATE_DIFFICULTY_LEVEL_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-CalculateDifficultyLevelFunction",
  JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-GetJotformSubmissionFunction",
  VECTOR_SEARCH_DEBUG_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-VectorSearchDebugFunction",
  CREATE_TEST_APPOINTMENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-CreateTestAppointmentFunction",
  AGENT_CHAT_FUNCTION_URL:
    "https://sxgi65fsfxmhzo5w2m4ksulvfy0pppfr.lambda-url.us-east-1.on.aws/",
  IMPERSONATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-stage-ImpersonationFunction",
  COMPLIANCE_REPORT_API_LAMBDA_FUNCTION: "ComplianceReportStack-stage-Api",
  PROMPT_EXECUTIONS_BUCKET_NAME: "indigo-prompt-executions-stage",

  API_SERVER_BASE_URL: "https://api-stage.getindigo.io",
  COLLAB_WS_URL: "wss://collab-stage.getindigo.io",
};

const prodConfig: Config = {
  RECORDS_BUCKET_NAME: "portal-medical-records090aa-prod",
  RESOURCES_BUCKET_NAME: "indigo-resources-prod",
  DOCTOR_PROFILES_BUCKET_NAME: "doctor-profiles-prod",
  TEMP_DOCUMENTS_BUCKET_NAME: "indigo-temp-documents-prod",
  RECORDS_DOC_FILE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateGoogleDocLinkFunction",
  GET_UNRESOLVED_COMMENTS_COUNT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GetUnresolvedCommentsCountFunction",
  RECORDS_CREATE_APPOINTMENT_DOCS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-CreateAppointmentDocsFunction",
  RECORDS_RESUMMARIZE_SEGMENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-ResummarizeSegmentFunction",
  RECORDS_SUMMARIZE_SUB_ROSA_ENTRY_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-SummarizeSubRosaEntryFunction",
  TRIAGE_V2_LAMBDA_FUNCTION: "",
  RECORDS_CREATE_EMPTY_MEDICAL_RECORD_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-CreateEmptyMedicalRecordFunction",
  BILLING_PDF_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateBillingFormFunction",
  EMAIL_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateEmailFunction",
  RECORDS_APPOINTMENT_PDF_PARSER_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-PdfParserFunction",
  HOI_GENERATION_DYNAMIC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateDynamicHoiFunction",
  IMPAIRMENT_RATING_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-ImpairmentRatingFunction",
  NOTICE_LETTER_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateNoticeLetterFunction",
  NOTIFICATION_FORM_GENERATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateNotificationFormFunction",
  DIAGNOSTIC_STUDY_PRESCRIPTION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateDiagnosticPrescriptionFunction",
  AUTO_GENERATE_DIAGNOSTIC_PRESCRIPTIONS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-AutoGenDiagnosticPrescriptionsFunction",
  RTW_VOUCHER_FORM_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateRtwVoucherFormFunction",
  DIAGNOSTIC_TEST_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateDiagnosticTestFunction",
  GENERATE_RECAP_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateRecapFunction",
  GENERATE_PDF_PACKAGE_FUNCTION:
    "IndigoLambdaStack-prod-GeneratePdfPackageFunction",
  GENERATE_REPORT_EMAIL_FUNCTION:
    "IndigoLambdaStack-prod-GenerateReportEmailFunction",
  HOI_APPEND_BODY_PARTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-HoiAppendBodyPartsFunction",
  DUPLICATE_GOOGLE_DOC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-DuplicateGoogleDocFunction",
  GENERATE_DIAGNOSTIC_PRESCRIPTION_LINK_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateDiagnosticPrescriptionLink",
  GENERATE_DIAGNOSTIC_TEST_FORM_GOOGLE_DOC_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateDiagnosticTestFormGoogleDoc",
  GENERATE_PAST_INJURY_MATRIX_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GeneratePastInjuryMatrixFunction",
  GENERATE_COVER_LETTER_RECAP_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateCoverLetterRecapFunction",
  CONSOLIDATE_PARTY_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-ConsolidatePartyFunction",
  GENERATE_INTRO_PARAGRAPH_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateIntroParagraphFunction",
  GENERATE_ALL_COVER_LETTER_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateAllCoverLetterFunction",
  GENERATE_RECORD_BRIEFING_GOOGLE_DOC_LINK_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateRecordBriefingGDocLinkFunction",
  GENERATE_RECORD_BRIEFING_DOCX_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateRecordBriefingDocxFunction",
  ASSESSMENT_REVIEW_GEN_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-AssessmentReviewGenFunction",
  ASSESSMENT_DICTATION_POPULATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-AssessmentDictationPopulateFunction",
  GENERATE_HISTORY_OF_INJURY_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateHistoryOfInjuryInsightsFunction",
  GENERATE_RECORD_BRIEFING_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateRecordBriefingInsightsFunction",
  GENERATE_PHYSICAL_EXAM_INSIGHTS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GeneratePhysicalExamInsightsFunction",
  GENERATE_PHYSICAL_EXAM_PREVIEW_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GeneratePhysicalExamPreviewFunction",
  GENERATE_PRE_HISTORY_TEMPLATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GeneratePreHistoryTemplateFunction",
  GENERATE_JOB_DUTIES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateJobDutiesFunction",
  FORMAT_ADDRESS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-FormatAddressFunction",
  RESOLVE_ATTACHMENT_REVIEW_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-ResolveAttachmentReviewFunction",
  MERGE_UPLOADED_FILES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-MergeUploadedFilesFunction",
  CORRECT_MEDICATIONS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-CorrectMedicationsFunction",
  GENERATE_HISTORY_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateHistoryValidationFunction",
  GEN_HISTORY_TRANSCRIPT_POPULATE_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenHistoryTranscriptPopulateFunc",
  GEN_HISTORY_MERGE_POPULATED_SOURCES_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenHistoryMergePopulatedSourcesFunc",
  ZENDESK_VALIDATE_TICKET_FUNCTION:
    "IndigoLambdaStack-prod-ValidateZendeskTicketFunction",
  EMIT_APPOINTMENT_EVENT_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-EmitAppointmentEventFunction",
  BACKFILL_ONSITE_OPS_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-BackfillOnsiteOpsFunction",
  GENERATE_MEDICAL_RECORD_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenerateMedicalRecordValidationFunction",
  GENERATE_HISTORY_MRR_CROSS_VALIDATION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GenHistoryMrrValidationFunc",
  CALCULATE_DIFFICULTY_LEVEL_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-CalculateDifficultyLevelFunction",
  JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-GetJotformSubmissionFunction",
  VECTOR_SEARCH_DEBUG_LAMBDA_FUNCTION:
    "IndigoLambdaStack-prod-VectorSearchDebugFunction",
  // Intentionally empty: test-appointment creation is dev/stage only.
  CREATE_TEST_APPOINTMENT_LAMBDA_FUNCTION: "",
  AGENT_CHAT_FUNCTION_URL:
    "https://wgp5bdoedaco42kwh32wcja2ze0knhxi.lambda-url.us-east-1.on.aws/",
  IMPERSONATION_LAMBDA_FUNCTION: "IndigoLambdaStack-prod-ImpersonationFunction",
  COMPLIANCE_REPORT_API_LAMBDA_FUNCTION: "ComplianceReportStack-prod-Api",
  PROMPT_EXECUTIONS_BUCKET_NAME: "indigo-prompt-executions-prod",

  API_SERVER_BASE_URL: "https://api.getindigo.io",
  COLLAB_WS_URL: "wss://collab.getindigo.io",
};

/**
 * localStorage key for the dev-only API server override.
 * Set/cleared by the debug-page Sandbox section (spec §12 of
 * specs/ecs-dev-sandboxes.md). Only consulted when env === "dev".
 */
export const API_SERVER_OVERRIDE_KEY = "indigo.apiServerBaseUrlOverride";

/**
 * localStorage key for the dev-only Indigo Doc collab-service override. Dev
 * points at the deployed `collab-dev.getindigo.io` like every other backend,
 * so this is how you reach a collab service you are running yourself:
 *
 *     localStorage.setItem("indigo.collabWsUrlOverride", "ws://localhost:1234")
 *
 * Only consulted when env === "dev".
 */
export const COLLAB_WS_OVERRIDE_KEY = "indigo.collabWsUrlOverride";

/**
 * localStorage key for the dev-only Indy (AgentChat) Function URL override —
 * a per-dev Lambda sandbox (spec §18 of specs/ecs-dev-sandboxes.md). Set by the
 * Sandbox switch in the Indy header. Only consulted when env === "dev", and
 * only honored for a URL in INDY_SANDBOX_URLS, so a stale value (e.g. from a
 * recreated sandbox) falls back to shared dev instead of a dead function.
 */
export const AGENT_CHAT_OVERRIDE_KEY = "indigo.agentChatUrlOverride";

export const getConfig = (): Config => {
  const currentEnv = getCurrentEnv();

  switch (currentEnv) {
    case "dev": {
      // Try/catch in case localStorage is unavailable (strict private mode,
      // SSR-style render). Fall back to the default devConfig silently —
      // a thrown getConfig() would break the entire app on startup.
      let apiServer: string | null = null;
      let collabWs: string | null = null;
      let agentChat: string | null = null;
      try {
        apiServer = localStorage.getItem(API_SERVER_OVERRIDE_KEY);
        collabWs = localStorage.getItem(COLLAB_WS_OVERRIDE_KEY);
        agentChat = localStorage.getItem(AGENT_CHAT_OVERRIDE_KEY);
      } catch {
        // ignore
      }
      return {
        ...devConfig,
        ...(apiServer ? { API_SERVER_BASE_URL: apiServer } : {}),
        ...(collabWs ? { COLLAB_WS_URL: collabWs } : {}),
        ...(agentChat && Object.values(INDY_SANDBOX_URLS).includes(agentChat)
          ? { AGENT_CHAT_FUNCTION_URL: agentChat }
          : {}),
      };
    }
    case "stage":
      return stageConfig;
    case "prod":
      return prodConfig;
  }
};
